/**
 * Authentication security utilities for handling edge cases and security threats
 */

import { errorHandler } from './errorHandling';

interface SessionData {
  userId: string;
  sessionId: string;
  lastActivity: number;
  createdAt: number;
  userAgent: string;
  ipAddress?: string;
  isActive: boolean;
}

interface SecurityEvent {
  type: 'suspicious_login' | 'concurrent_session' | 'session_timeout' | 'unusual_activity';
  userId: string;
  sessionId: string;
  timestamp: number;
  metadata: Record<string, any>;
}

class AuthSecurityManager {
  private static instance: AuthSecurityManager;
  private sessions: Map<string, SessionData> = new Map();
  private securityEvents: SecurityEvent[] = [];
  private maxConcurrentSessions = 3;
  private sessionTimeoutMs = 30 * 60 * 1000; // 30 minutes
  private suspiciousActivityThreshold = 10; // max actions per minute
  private activityTracking: Map<string, number[]> = new Map();

  public static getInstance(): AuthSecurityManager {
    if (!AuthSecurityManager.instance) {
      AuthSecurityManager.instance = new AuthSecurityManager();
    }
    return AuthSecurityManager.instance;
  }

  /**
   * Initialize a new session
   */
  public initializeSession(userId: string, userAgent: string, ipAddress?: string): string {
    const sessionId = this.generateSessionId();
    const now = Date.now();

    // Check for concurrent sessions
    this.enforceConcurrentSessionLimit(userId);

    const sessionData: SessionData = {
      userId,
      sessionId,
      lastActivity: now,
      createdAt: now,
      userAgent,
      ipAddress,
      isActive: true,
    };

    this.sessions.set(sessionId, sessionData);
    
    // Log session creation
    this.logSecurityEvent({
      type: 'concurrent_session',
      userId,
      sessionId,
      timestamp: now,
      metadata: { action: 'session_created', userAgent, ipAddress },
    });

    console.log(`Session initialized for user ${userId}: ${sessionId}`);
    return sessionId;
  }

  /**
   * Update session activity
   */
  public updateSessionActivity(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session || !session.isActive) {
      return false;
    }

    const now = Date.now();
    
    // Check if session has timed out
    if (now - session.lastActivity > this.sessionTimeoutMs) {
      this.terminateSession(sessionId, 'timeout');
      return false;
    }

    // Check for suspicious activity
    if (this.detectSuspiciousActivity(session.userId)) {
      this.terminateSession(sessionId, 'suspicious_activity');
      return false;
    }

    session.lastActivity = now;
    return true;
  }

  /**
   * Terminate a session
   */
  public terminateSession(sessionId: string, reason: string = 'manual'): void {
    const session = this.sessions.get(sessionId);
    if (session) {
      session.isActive = false;
      
      this.logSecurityEvent({
        type: 'session_timeout',
        userId: session.userId,
        sessionId,
        timestamp: Date.now(),
        metadata: { reason },
      });

      // Keep session data for a short time for audit purposes
      setTimeout(() => {
        this.sessions.delete(sessionId);
      }, 5 * 60 * 1000); // 5 minutes

      console.log(`Session terminated: ${sessionId} (reason: ${reason})`);
    }
  }

  /**
   * Terminate all sessions for a user
   */
  public terminateAllUserSessions(userId: string, exceptSessionId?: string): void {
    for (const [sessionId, session] of this.sessions.entries()) {
      if (session.userId === userId && sessionId !== exceptSessionId) {
        this.terminateSession(sessionId, 'force_logout');
      }
    }
  }

  /**
   * Check if session is valid
   */
  public isSessionValid(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session || !session.isActive) {
      return false;
    }

    // Check timeout
    const now = Date.now();
    if (now - session.lastActivity > this.sessionTimeoutMs) {
      this.terminateSession(sessionId, 'timeout');
      return false;
    }

    return true;
  }

  /**
   * Get session data
   */
  public getSession(sessionId: string): SessionData | undefined {
    return this.sessions.get(sessionId);
  }

  /**
   * Get all active sessions for a user
   */
  public getUserSessions(userId: string): SessionData[] {
    return Array.from(this.sessions.values()).filter(
      session => session.userId === userId && session.isActive
    );
  }

  /**
   * Enforce concurrent session limit
   */
  private enforceConcurrentSessionLimit(userId: string): void {
    const userSessions = this.getUserSessions(userId);
    
    if (userSessions.length >= this.maxConcurrentSessions) {
      // Terminate oldest sessions
      const sortedSessions = userSessions.sort((a, b) => a.lastActivity - b.lastActivity);
      const sessionsToTerminate = sortedSessions.slice(0, userSessions.length - this.maxConcurrentSessions + 1);
      
      for (const session of sessionsToTerminate) {
        this.terminateSession(session.sessionId, 'concurrent_limit');
      }

      this.logSecurityEvent({
        type: 'concurrent_session',
        userId,
        sessionId: 'system',
        timestamp: Date.now(),
        metadata: { 
          action: 'limit_enforced', 
          terminatedSessions: sessionsToTerminate.length 
        },
      });
    }
  }

  /**
   * Detect suspicious activity
   */
  private detectSuspiciousActivity(userId: string): boolean {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    
    // Get activity for the last minute
    const userActivity = this.activityTracking.get(userId) || [];
    const recentActivity = userActivity.filter(timestamp => timestamp > oneMinuteAgo);
    
    // Update activity tracking
    recentActivity.push(now);
    this.activityTracking.set(userId, recentActivity);

    // Check if activity exceeds threshold
    if (recentActivity.length > this.suspiciousActivityThreshold) {
      this.logSecurityEvent({
        type: 'suspicious_login',
        userId,
        sessionId: 'system',
        timestamp: now,
        metadata: { 
          activityCount: recentActivity.length,
          threshold: this.suspiciousActivityThreshold 
        },
      });

      return true;
    }

    return false;
  }

  /**
   * Generate secure session ID
   */
  private generateSessionId(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Log security event
   */
  private logSecurityEvent(event: SecurityEvent): void {
    this.securityEvents.push(event);
    
    // Keep only last 1000 events
    if (this.securityEvents.length > 1000) {
      this.securityEvents.splice(0, this.securityEvents.length - 1000);
    }

    // Log to error handler for monitoring
    if (event.type === 'suspicious_login' || event.type === 'unusual_activity') {
      errorHandler.logApiError(new Error(`Security event: ${event.type}`), {
        securityEvent: true,
        userId: event.userId,
        eventType: event.type,
        metadata: event.metadata,
      });
    }

    // In development, log to console
    if (import.meta.env.DEV) {
      console.warn('🔒 Security Event:', event);
    }
  }

  /**
   * Get security events for a user
   */
  public getSecurityEvents(userId: string, limit: number = 50): SecurityEvent[] {
    return this.securityEvents
      .filter(event => event.userId === userId)
      .slice(-limit);
  }

  /**
   * Clean up expired sessions and old events
   */
  public cleanup(): void {
    const now = Date.now();
    
    // Clean up expired sessions
    for (const [sessionId, session] of this.sessions.entries()) {
      if (now - session.lastActivity > this.sessionTimeoutMs) {
        this.terminateSession(sessionId, 'cleanup');
      }
    }

    // Clean up old activity tracking
    for (const [userId, activities] of this.activityTracking.entries()) {
      const recentActivities = activities.filter(timestamp => now - timestamp < 3600000); // Keep 1 hour
      if (recentActivities.length === 0) {
        this.activityTracking.delete(userId);
      } else {
        this.activityTracking.set(userId, recentActivities);
      }
    }

    // Clean up old security events (keep only last 24 hours)
    const oneDayAgo = now - 24 * 60 * 60 * 1000;
    this.securityEvents = this.securityEvents.filter(event => event.timestamp > oneDayAgo);
  }

  /**
   * Configure security settings
   */
  public configure(settings: {
    maxConcurrentSessions?: number;
    sessionTimeoutMs?: number;
    suspiciousActivityThreshold?: number;
  }): void {
    if (settings.maxConcurrentSessions !== undefined) {
      this.maxConcurrentSessions = settings.maxConcurrentSessions;
    }
    if (settings.sessionTimeoutMs !== undefined) {
      this.sessionTimeoutMs = settings.sessionTimeoutMs;
    }
    if (settings.suspiciousActivityThreshold !== undefined) {
      this.suspiciousActivityThreshold = settings.suspiciousActivityThreshold;
    }
  }

  /**
   * Force logout user on security breach
   */
  public forceLogoutUser(userId: string, reason: string): void {
    this.terminateAllUserSessions(userId);
    
    this.logSecurityEvent({
      type: 'unusual_activity',
      userId,
      sessionId: 'system',
      timestamp: Date.now(),
      metadata: { action: 'force_logout', reason },
    });

    // Clear activity tracking for this user
    this.activityTracking.delete(userId);
  }
}

// Singleton instance
export const authSecurityManager = AuthSecurityManager.getInstance();

/**
 * Initialize auth security with cleanup interval
 */
export const initializeAuthSecurity = (config?: {
  maxConcurrentSessions?: number;
  sessionTimeoutMs?: number;
  suspiciousActivityThreshold?: number;
}): void => {
  if (config) {
    authSecurityManager.configure(config);
  }

  // Set up cleanup interval (every 5 minutes)
  if (typeof window !== 'undefined') {
    setInterval(() => {
      authSecurityManager.cleanup();
    }, 5 * 60 * 1000);
  }

  console.log('Auth security manager initialized');
};

/**
 * Session activity tracker hook for Auth0
 */
export const createAuth0SecurityWrapper = () => {
  return {
    onLogin: (user: any) => {
      if (user?.sub) {
        const sessionId = authSecurityManager.initializeSession(
          user.sub,
          navigator.userAgent,
          // IP address would be determined by backend
        );
        
        // Store session ID for later use
        sessionStorage.setItem('auth_session_id', sessionId);
      }
    },

    onLogout: () => {
      const sessionId = sessionStorage.getItem('auth_session_id');
      if (sessionId) {
        authSecurityManager.terminateSession(sessionId, 'user_logout');
        sessionStorage.removeItem('auth_session_id');
      }
    },

    onActivity: () => {
      const sessionId = sessionStorage.getItem('auth_session_id');
      if (sessionId) {
        const isValid = authSecurityManager.updateSessionActivity(sessionId);
        if (!isValid) {
          // Session is no longer valid, force logout
          sessionStorage.removeItem('auth_session_id');
          window.location.href = '/login';
        }
      }
    },

    checkSessionHealth: (): boolean => {
      const sessionId = sessionStorage.getItem('auth_session_id');
      if (!sessionId) return false;
      
      return authSecurityManager.isSessionValid(sessionId);
    },

    getSessionInfo: () => {
      const sessionId = sessionStorage.getItem('auth_session_id');
      if (!sessionId) return null;
      
      return authSecurityManager.getSession(sessionId);
    },
  };
};

/**
 * Activity tracker to detect user interactions
 */
export const setupActivityTracking = (): void => {
  if (typeof window === 'undefined') return;

  const wrapper = createAuth0SecurityWrapper();
  
  // Track various user activities
  const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  
  let lastActivity = Date.now();
  const throttleMs = 10000; // Only track activity every 10 seconds
  
  const trackActivity = () => {
    const now = Date.now();
    if (now - lastActivity > throttleMs) {
      wrapper.onActivity();
      lastActivity = now;
    }
  };

  // Add event listeners
  activityEvents.forEach(event => {
    document.addEventListener(event, trackActivity, { passive: true });
  });

  // Check session health periodically
  setInterval(() => {
    if (!wrapper.checkSessionHealth()) {
      console.warn('Session health check failed - user may need to re-authenticate');
    }
  }, 60000); // Check every minute
}; 